// Shared TIN handling for BIR DAT (SAWT) exports.
//
// BIR TINs are natively 9 digits and legitimately carry leading zeros
// (e.g. 000456789). Storage strips them, so the pad must be on the LEFT:
// padEnd would turn 456789 into 456789000 — a different taxpayer.
//
// Presence is the only validation here. BIR's own checksum/format rules are
// unconfirmed, and a wrong check would block valid filings.

/**
 * Normalize a TIN to 9 digits. Returns null when no digits are present —
 * callers must treat null as a blocking error, never as a value to emit.
 */
export function cleanTIN(tin) {
  const digits = String(tin ?? '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.padStart(9, '0').slice(0, 9);
}

/**
 * Names the payees whose TIN is missing.
 * @param {Array<{name?: string, tin?: unknown}>} payees — in export row order
 * @returns {string[]} display labels, deduped, row order preserved
 */
export function findMissingTINs(payees) {
  const seen = new Set();
  const missing = [];
  payees.forEach(({ name, tin }, i) => {
    if (cleanTIN(tin)) return;
    const label = String(name || '').trim() || `(unnamed payee, row ${i + 1})`;
    if (seen.has(label)) return;
    seen.add(label);
    missing.push(label);
  });
  return missing;
}

/** Human-readable detail for a blocked export. */
export function missingTINMessage(missing) {
  const shown = missing.slice(0, 10);
  const rest = missing.length - shown.length;
  const who = shown.join(', ') + (rest > 0 ? `, and ${rest} more` : '');
  return `Missing TIN for ${missing.length} payee${missing.length === 1 ? '' : 's'}: ${who}. `
    + 'Add each TIN to the payee record, then export again.';
}
